import { useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { AlertCircle, ExternalLink, RefreshCw, Search } from 'lucide-react';
import { format } from 'date-fns';

import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { DataTableSkeleton } from '@/components/ui/data-table-skeleton';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AtsBadge } from '@/components/scout/ScoutStatusBadge';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useScoutPrograms, useScoutTargets, useUpdateScoutTarget } from '@/hooks/useScout';
import type { ScoutTarget } from '@repo/types';

export const Route = createFileRoute('/admin/scout/targets')({
  component: ScoutTargetsPage,
});


/**
 * The company name, editable in place.
 *
 * This string is not decoration: it is interpolated into the agent's prompt as
 * the employer ("You are on the careers site of X"), so a wrong one tells the
 * model something false about every posting it finds there. It is derived from
 * the URL and, for tenants whose URL names nobody, read off the site — both
 * best-effort, and 10 targets resolve by neither. This is the correction.
 *
 * Saves on blur or Enter, reverts on Escape. Submitting an empty value is
 * allowed and meaningful: it clears a wrong name back to "unnamed", which makes
 * the prompt say "this company" instead of naming the wrong one.
 */
function TargetNameCell({ target }: { target: ScoutTarget }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(target.company);
  const update = useUpdateScoutTarget();
  // Blur fires on Escape too, and would save the value we just abandoned.
  const cancelled = useRef(false);

  const commit = () => {
    setEditing(false);
    const next = value.trim();
    if (next === target.company) return;
    update.mutate({ id: target.id, company: next });
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setValue(target.company);
          setEditing(true);
        }}
        className="rounded text-left hover:underline"
        title="Rename this target"
      >
        {target.company ? (
          <span className="font-medium">{target.company}</span>
        ) : (
          <span className="font-medium italic text-muted-foreground">Unnamed</span>
        )}
      </button>
    );
  }

  return (
    <Input
      autoFocus
      value={value}
      disabled={update.isPending}
      placeholder="Company name"
      className="h-7 w-[200px] text-sm"
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (cancelled.current) {
          cancelled.current = false;
          return;
        }
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          cancelled.current = true;
          setValue(target.company);
          setEditing(false);
        }
      }}
    />
  );
}

function ScoutTargetsPage() {
  const [search, setSearch] = useState('');
  const [programFilter, setProgramFilter] = useState<string>('all');
  const [atsFilter, setAtsFilter] = useState<string>('all');

  const debouncedSearch = useDebouncedValue(search, 300);

  const { data: programs } = useScoutPrograms();

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      programIds: programFilter !== 'all' ? [programFilter] : undefined,
      ats: atsFilter !== 'all' ? atsFilter : undefined,
    }),
    [debouncedSearch, programFilter, atsFilter]
  );

  const { data, isLoading, isFetching, error, refetch } = useScoutTargets(filters);
  const targets = useMemo(() => data ?? [], [data]);

  // Platform options come from the data rather than a hardcoded list, so a new
  // ATS rule in the classifier shows up here without a change on this page.
  const atsOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const target of targets) counts.set(target.ats, (counts.get(target.ats) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [targets]);

  const columns = useMemo<ColumnDef<ScoutTarget>[]>(
    () => [
      {
        accessorKey: 'company',
        header: 'Company',
        cell: ({ row }) => (
          <div className="min-w-0 space-y-0.5">
            <div className="flex items-center gap-2">
              <TargetNameCell target={row.original} />
              {!row.original.active && (
                <Badge variant="outline" className="text-muted-foreground">
                  Inactive
                </Badge>
              )}
            </div>
            <a
              href={row.original.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-[420px] items-center gap-1 truncate text-xs text-muted-foreground hover:underline"
              title={row.original.url}
            >
              <span className="truncate">{row.original.url}</span>
              <ExternalLink className="size-3 shrink-0" />
            </a>
          </div>
        ),
      },
      {
        accessorKey: 'ats',
        header: 'Platform',
        cell: ({ row }) => <AtsBadge ats={row.original.ats} />,
      },
      {
        accessorKey: 'requiresSearch',
        header: 'Needs search',
        cell: ({ row }) =>
          row.original.requiresSearch ? (
            <Badge variant="outline" className="font-medium">
              Yes
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">No</span>
          ),
      },
      {
        accessorKey: 'consecutiveFailures',
        header: 'Failures',
        cell: ({ row }) =>
          row.original.consecutiveFailures > 0 ? (
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              {row.original.consecutiveFailures}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'lastScrapedAt',
        header: 'Last scouted',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.lastScrapedAt
              ? format(new Date(row.original.lastScrapedAt), 'd MMM yyyy')
              : 'Never'}
          </span>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: targets,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <div className="space-y-6 p-4 duration-500 ease-out animate-in fade-in slide-in-from-bottom-6 sm:p-6">
      <PageHeader
        title="Scout Targets"
        description="The curated career sites the Job Scout crawls, grouped by the programs they hire for."
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
            Refresh
          </Button>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by company or URL…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Select value={programFilter} onValueChange={setProgramFilter}>
            <SelectTrigger className="min-w-0 flex-1 sm:w-[220px] sm:flex-none">
              <SelectValue placeholder="All programs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programs</SelectItem>
              {(programs ?? []).map((program) => (
                <SelectItem key={program.id} value={program.id}>
                  {program.name} ({program.targetCount})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={atsFilter} onValueChange={setAtsFilter}>
            <SelectTrigger className="min-w-0 flex-1 sm:w-[180px] sm:flex-none">
              <SelectValue placeholder="All platforms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All platforms</SelectItem>
              {atsOptions.map(([ats, count]) => (
                <SelectItem key={ats} value={ats}>
                  {ats.replace(/_/g, ' ')} ({count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{error.message || 'Failed to load scout targets.'}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <DataTableSkeleton columns={5} />
      ) : (
        <>
          <DataTable table={table} />
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{targets.length}</span> career site
            {targets.length === 1 ? '' : 's'}
          </p>
        </>
      )}
    </div>
  );
}
